// Group (club) detail page. The viewer's own membership row drives every
// affordance: join/request/accept/leave in the header, admin controls in the
// members tab. RLS enforces all of it server-side — the UI only decides what
// to offer. A HIDDEN club resolves to null for non-members, but an INVITED
// person still sees their own clubMember row, so the "not found" path first
// checks for a pending invitation to render an accept/decline card.
import { useState, type ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useLocation } from "wouter";
import { CalendarDays, UsersRound } from "lucide-react";
import { gqlRequest } from "../lib/gql";
import {
  CLUB_DETAIL,
  CREATE_CLUB_MEMBER,
  DELETE_CLUB,
  DELETE_CLUB_MEMBER,
  MY_CLUB_MEMBERSHIPS,
  UPCOMING_EVENTS_FOR_CLUB,
  UPDATE_CLUB_MEMBER,
  type ClubDetailResult,
  type ClubMemberNode,
  type ClubMembershipRow,
  type MyClubMembershipsResult,
  type PersonSummary,
  type UpcomingEventsForClubResult,
} from "../lib/queries";
import { useAuth } from "../lib/auth";
import {
  CLUB_VISIBILITY_ICONS,
  formatDate,
  formatDateRange,
  type ClubMemberRole,
  type ClubMemberStatus,
} from "../lib/format";
import { useT } from "../lib/i18n";
import { EntityTabs } from "../components/EntityTabs";
import { ShareButton } from "../components/ShareButton";
import { OutingCard } from "../components/OutingCard";
import { PersonPicker } from "../components/PersonPicker";
import { Avatar } from "../components/Avatar";
import { ActivityList, EmptyState, ErrorBox, Spinner } from "../components/ui";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

export function GroupPage({ id }: { id: string }) {
  const { personId } = useAuth();
  const { t, labelizeT } = useT();
  const [, navigate] = useLocation();
  const queryClient = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: ["club", id],
    queryFn: () => gqlRequest<ClubDetailResult>(CLUB_DETAIL, { id }),
  });

  // The viewer's own row, picked client-side from the personId-keyed
  // memberships query (shared with the outing share control).
  const memberships = useQuery({
    queryKey: ["my-club-memberships", personId],
    enabled: !!personId,
    queryFn: () => gqlRequest<MyClubMembershipsResult>(MY_CLUB_MEMBERSHIPS, { personId }),
  });
  const myRow: ClubMembershipRow | null =
    memberships.data?.clubMembers.nodes.find((m) => m.clubId === id) ?? null;

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ["club", id] });
    queryClient.invalidateQueries({ queryKey: ["my-club-memberships"] });
  };

  const join = useMutation({
    mutationFn: (status: "ACTIVE" | "PENDING") =>
      gqlRequest(CREATE_CLUB_MEMBER, { clubId: id, personId, role: "MEMBER", status }),
    onSuccess: invalidate,
  });

  const patchMember = useMutation({
    mutationFn: (vars: { memberId: string; patch: { role?: ClubMemberRole; status?: ClubMemberStatus } }) =>
      gqlRequest(UPDATE_CLUB_MEMBER, { id: vars.memberId, patch: vars.patch }),
    onSuccess: invalidate,
  });

  const removeMember = useMutation({
    mutationFn: (memberId: string) => gqlRequest(DELETE_CLUB_MEMBER, { id: memberId }),
    onSuccess: invalidate,
  });

  const invite = useMutation({
    mutationFn: (p: PersonSummary) =>
      gqlRequest(CREATE_CLUB_MEMBER, { clubId: id, personId: p.id, role: "MEMBER", status: "INVITED" }),
    onSuccess: invalidate,
  });

  const deleteClub = useMutation({
    mutationFn: () => gqlRequest(DELETE_CLUB, { id }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["my-club-memberships"] });
      navigate("/");
    },
  });

  if (isLoading || (personId && memberships.isLoading)) return <Spinner />;
  if (error) return <ErrorBox error={error} />;

  const club = data?.club;
  if (!club) {
    // A hidden club is invisible until the invite is accepted, but the
    // viewer's own INVITED row is not — offer accept/decline from it alone.
    if (myRow?.status === "INVITED") {
      return (
        <div className="mx-auto m-4 max-w-md rounded-xl border bg-card p-6 text-center text-card-foreground shadow-card">
          <p className="mb-4 text-sm text-foreground">{t("group.invitedBanner")}</p>
          <div className="flex justify-center gap-3">
            <Button
              type="button"
              onClick={() => patchMember.mutate({ memberId: myRow.id, patch: { role: "MEMBER", status: "ACTIVE" } })}
              disabled={patchMember.isPending}
            >
              {t("group.acceptInvite")}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => removeMember.mutate(myRow.id)}
              disabled={removeMember.isPending}
            >
              {t("group.decline")}
            </Button>
          </div>
          {(patchMember.error ?? removeMember.error) != null && (
            <ErrorBox error={patchMember.error ?? removeMember.error} />
          )}
        </div>
      );
    }
    return <EmptyState>{t("group.notFound")}</EmptyState>;
  }

  const isActiveMember = myRow != null && myRow.status === "ACTIVE";
  const isAdmin = isActiveMember && (myRow.role === "OWNER" || myRow.role === "ADMIN");
  const isOwner = isActiveMember && myRow.role === "OWNER";

  const members = club.clubMembersByClubId.nodes;
  const activeMembers = members.filter((m) => m.status === "ACTIVE");
  const pendingMembers = members.filter((m) => m.status === "PENDING");
  const invitedMembers = members.filter((m) => m.status === "INVITED");
  const bannedMembers = members.filter((m) => m.status === "BANNED");

  const outings = club.clubOutingsByClubId.nodes
    .map((n) => n.outing)
    .filter((o) => o != null);

  const VisibilityIcon = CLUB_VISIBILITY_ICONS[club.visibility];

  const actionError =
    join.error ?? patchMember.error ?? removeMember.error ?? invite.error ?? deleteClub.error;

  const membershipControls = (() => {
    if (!personId) {
      // Same pattern as FollowButton: anonymous visitors get a login link,
      // not a disabled button.
      if (club.joinPolicy === "INVITE_ONLY")
        return <p className="text-sm text-muted-foreground">{t("group.inviteOnlyNote")}</p>;
      return (
        <Button asChild variant="outline">
          <Link href={`/login?next=${encodeURIComponent(`/group/${id}`)}`}>
            {club.joinPolicy === "OPEN" ? t("group.join") : t("group.requestJoin")}
          </Link>
        </Button>
      );
    }
    if (!myRow) {
      if (club.joinPolicy === "OPEN")
        return (
          <Button type="button" onClick={() => join.mutate("ACTIVE")} disabled={join.isPending}>
            {t("group.join")}
          </Button>
        );
      if (club.joinPolicy === "REQUEST_TO_JOIN")
        return (
          <Button type="button" onClick={() => join.mutate("PENDING")} disabled={join.isPending}>
            {t("group.requestJoin")}
          </Button>
        );
      return <p className="text-sm text-muted-foreground">{t("group.inviteOnlyNote")}</p>;
    }
    switch (myRow.status) {
      case "PENDING":
        return (
          <span className="inline-flex items-center gap-2">
            <Badge variant="secondary" className="text-muted-foreground">
              {t("group.requestPending")}
            </Badge>
            <Button
              type="button"
              variant="outline"
              onClick={() => removeMember.mutate(myRow.id)}
              disabled={removeMember.isPending}
            >
              {t("group.withdrawRequest")}
            </Button>
          </span>
        );
      case "INVITED":
        return (
          <span className="inline-flex items-center gap-2">
            <Button
              type="button"
              onClick={() => patchMember.mutate({ memberId: myRow.id, patch: { role: "MEMBER", status: "ACTIVE" } })}
              disabled={patchMember.isPending}
            >
              {t("group.acceptInvite")}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => removeMember.mutate(myRow.id)}
              disabled={removeMember.isPending}
            >
              {t("group.decline")}
            </Button>
          </span>
        );
      case "BANNED":
        return <p className="text-sm text-muted-foreground">{t("group.bannedNote")}</p>;
      case "ACTIVE":
        // The owner cannot leave (RLS rejects deleting the owner row).
        if (isOwner) return null;
        return (
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              if (window.confirm(t("group.confirmLeave"))) removeMember.mutate(myRow.id);
            }}
            disabled={removeMember.isPending}
          >
            {t("group.leave")}
          </Button>
        );
    }
  })();

  const header = (
    <div>
      <p className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-primary">
        <UsersRound aria-hidden className="size-3.5" />
        {t("group.kicker")} · {labelizeT(club.visibility)}
      </p>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h1 className="flex items-center gap-2.5 text-2xl font-semibold tracking-tight text-foreground">
          <Avatar person={{ displayName: club.name, avatarKey: club.avatarKey }} size="md" />
          {club.name}
        </h1>
        <div className="flex flex-wrap items-center gap-2">
          <ShareButton title={club.name} />
          {isAdmin && (
            <Button asChild variant="outline">
              <Link href={`/group/${club.id}/edit`}>{t("group.edit")}</Link>
            </Button>
          )}
          {isOwner && (
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                if (window.confirm(t("group.confirmDelete"))) deleteClub.mutate();
              }}
              disabled={deleteClub.isPending}
              className="border-destructive/30 text-destructive hover:border-destructive/50 hover:bg-destructive/10 hover:text-destructive"
            >
              {deleteClub.isPending ? t("common.deleting") : t("common.delete")}
            </Button>
          )}
          {membershipControls}
        </div>
      </div>
      <p className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-muted-foreground">
        <span className="inline-flex items-center gap-1">
          <VisibilityIcon aria-hidden className="size-3.5" />
          {labelizeT(club.joinPolicy)}
        </span>
        <span>
          · {t(activeMembers.length === 1 ? "group.membersOne" : "group.membersOther", { count: activeMembers.length })}
        </span>
        {club.activities && club.activities.length > 0 && (
          <span className="inline-flex items-center gap-1">
            · <ActivityList activities={club.activities} />
          </span>
        )}
        {isActiveMember && myRow && (
          <Badge className="bg-primary/10 text-primary">{labelizeT(myRow.role)}</Badge>
        )}
      </p>
      {actionError != null && <ErrorBox error={actionError} />}
    </div>
  );

  const infoTab = (
    <div className="space-y-4">
      {club.description ? (
        <p className="whitespace-pre-line text-foreground">{club.description}</p>
      ) : (
        <p className="text-sm text-muted-foreground">{t("group.noDescription")}</p>
      )}
      <dl className="grid grid-cols-2 gap-3 sm:grid-cols-3">
        <Stat label={t("tabs.members")} value={String(activeMembers.length)} />
        <Stat label={t("tabs.outings")} value={String(club.clubOutingsByClubId.totalCount)} />
        <Stat label={t("form.visibility")} value={labelizeT(club.visibility)} />
        <Stat label={t("group.joinPolicy")} value={labelizeT(club.joinPolicy)} />
      </dl>
      <p className="flex items-center gap-1.5 text-sm text-muted-foreground">
        <CalendarDays aria-hidden className="size-3.5" />
        {t("group.created", { date: formatDate(club.createdAt.slice(0, 10)) })}
      </p>
    </div>
  );

  const membersTab = (
    <div className="space-y-5">
      {activeMembers.length > 0 ? (
        <ul className="divide-y overflow-hidden rounded-xl border bg-card text-card-foreground shadow-card">
          {activeMembers.map((m) => (
            <MemberRow
              key={m.id}
              member={m}
              // Admins manage everyone except the owner row and themselves.
              actions={
                isAdmin && m.role !== "OWNER" && m.personId !== personId ? (
                  <>
                    <MemberAction
                      label={m.role === "ADMIN" ? t("group.demote") : t("group.promote")}
                      onClick={() =>
                        patchMember.mutate({ memberId: m.id, patch: { role: m.role === "ADMIN" ? "MEMBER" : "ADMIN" } })
                      }
                      disabled={patchMember.isPending}
                    />
                    <MemberAction
                      label={t("group.ban")}
                      destructive
                      onClick={() => {
                        if (window.confirm(t("group.confirmBan", { name: memberName(m, t) })))
                          patchMember.mutate({ memberId: m.id, patch: { status: "BANNED" } });
                      }}
                      disabled={patchMember.isPending}
                    />
                    <MemberAction
                      label={t("common.remove")}
                      destructive
                      onClick={() => {
                        if (window.confirm(t("group.confirmKick", { name: memberName(m, t) })))
                          removeMember.mutate(m.id);
                      }}
                      disabled={removeMember.isPending}
                    />
                  </>
                ) : null
              }
            />
          ))}
        </ul>
      ) : (
        // RLS returns no member rows of PRIVATE/HIDDEN clubs to non-members.
        <EmptyState>{t("group.membersHidden")}</EmptyState>
      )}

      {isAdmin && pendingMembers.length > 0 && (
        <MemberSection title={t("group.pendingRequests")}>
          {pendingMembers.map((m) => (
            <MemberRow
              key={m.id}
              member={m}
              actions={
                <>
                  <MemberAction
                    label={t("group.approve")}
                    onClick={() => patchMember.mutate({ memberId: m.id, patch: { status: "ACTIVE" } })}
                    disabled={patchMember.isPending}
                  />
                  <MemberAction
                    label={t("group.decline")}
                    destructive
                    onClick={() => removeMember.mutate(m.id)}
                    disabled={removeMember.isPending}
                  />
                </>
              }
            />
          ))}
        </MemberSection>
      )}

      {isAdmin && invitedMembers.length > 0 && (
        <MemberSection title={labelizeT("INVITED")}>
          {invitedMembers.map((m) => (
            <MemberRow
              key={m.id}
              member={m}
              actions={
                <MemberAction
                  label={t("common.remove")}
                  destructive
                  onClick={() => removeMember.mutate(m.id)}
                  disabled={removeMember.isPending}
                />
              }
            />
          ))}
        </MemberSection>
      )}

      {isAdmin && bannedMembers.length > 0 && (
        <MemberSection title={labelizeT("BANNED")}>
          {bannedMembers.map((m) => (
            <MemberRow
              key={m.id}
              member={m}
              actions={
                <MemberAction
                  label={t("group.unban")}
                  onClick={() => removeMember.mutate(m.id)}
                  disabled={removeMember.isPending}
                />
              }
            />
          ))}
        </MemberSection>
      )}

      {isAdmin && (
        <div>
          <h3 className="mb-2 text-sm font-semibold text-foreground">{t("group.invite")}</h3>
          {/* Picking a person invites them immediately — the picker's selected
              list stays empty on purpose. */}
          <PersonPicker
            selected={[]}
            onChange={(next) => {
              const p = next[next.length - 1];
              if (p) invite.mutate(p);
            }}
            excludeIds={members.map((m) => m.personId)}
          />
        </div>
      )}
    </div>
  );

  const outingsTab =
    outings.length > 0 ? (
      <div className="space-y-3">
        {outings.map((o) => (
          <OutingCard key={o.id} outing={o} />
        ))}
      </div>
    ) : (
      <EmptyState>{t("group.noOutings")}</EmptyState>
    );

  return (
    <EntityTabs
      header={header}
      tabs={[
        { key: "info", label: t("tabs.info"), content: infoTab },
        { key: "members", label: t("tabs.members"), badge: activeMembers.length, content: membersTab },
        { key: "outings", label: t("tabs.outings"), badge: club.clubOutingsByClubId.totalCount, content: outingsTab },
        {
          key: "events",
          label: t("tabs.events"),
          badge: club.eventsByClubId.totalCount,
          content: <ClubEventsTab clubId={club.id} canCreate={isActiveMember} />,
        },
      ]}
    />
  );
}

/**
 * Upcoming events of this club, soonest first. Active members get a "New
 * event" button that preselects the club on the event form.
 */
function ClubEventsTab({ clubId, canCreate }: { clubId: string; canCreate: boolean }) {
  const { t, labelizeT } = useT();
  // Captured once per mount so the query key doesn't churn every render.
  const [from] = useState(() => new Date().toISOString());

  const { data, isLoading, error } = useQuery({
    queryKey: ["club-events", clubId, from],
    queryFn: () => gqlRequest<UpcomingEventsForClubResult>(UPCOMING_EVENTS_FOR_CLUB, { clubId, from }),
  });

  if (isLoading) return <Spinner />;
  if (error) return <ErrorBox error={error} />;

  const events = data?.events.nodes ?? [];

  return (
    <div className="space-y-3">
      {canCreate && (
        <div className="flex justify-end">
          <Button asChild>
            <Link href={`/event/new?club=${clubId}`}>{t("group.newEvent")}</Link>
          </Button>
        </div>
      )}
      {events.length > 0 ? (
        <ul className="divide-y overflow-hidden rounded-xl border bg-card text-card-foreground shadow-card">
          {events.map((e) => (
            <li key={e.id}>
              <Link
                href={`/event/${e.id}`}
                className="flex items-center gap-3 px-4 py-3 transition-colors hover:bg-accent"
              >
                <CalendarDays aria-hidden className="size-4 shrink-0 text-muted-foreground" />
                <span className="min-w-0 flex-1">
                  <span className="flex flex-wrap items-center gap-1.5 font-medium text-foreground">
                    {e.title}
                    <Badge variant="secondary" className="font-semibold text-muted-foreground">
                      {labelizeT(e.joinPolicy)}
                    </Badge>
                  </span>
                  <span className="block text-xs text-muted-foreground">
                    {formatDateRange(e.startsAt, e.endsAt)}
                  </span>
                </span>
              </Link>
            </li>
          ))}
        </ul>
      ) : (
        <EmptyState>{t("group.noUpcomingEvents")}</EmptyState>
      )}
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-xl border bg-muted/50 p-3">
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className="text-base font-semibold text-foreground">{value}</dd>
    </div>
  );
}

type Translate = ReturnType<typeof useT>["t"];

function memberName(m: ClubMemberNode, t: Translate): string {
  return m.person?.displayName ?? t("common.someone");
}

function MemberSection({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div>
      <h3 className="mb-2 text-sm font-semibold text-foreground">{title}</h3>
      <ul className="divide-y overflow-hidden rounded-xl border bg-card text-card-foreground shadow-card">{children}</ul>
    </div>
  );
}

function MemberRow({ member, actions }: { member: ClubMemberNode; actions?: ReactNode }) {
  const { t, labelizeT } = useT();
  return (
    <li className="flex flex-wrap items-center gap-2 px-4 py-3">
      <Avatar
        person={{ displayName: member.person?.displayName ?? null, avatarKey: member.person?.avatarKey }}
        size="sm"
      />
      <Link href={`/person/${member.personId}`} className="font-medium text-primary hover:underline">
        {memberName(member, t)}
      </Link>
      {member.status === "ACTIVE" && member.role !== "MEMBER" && (
        <Badge className="bg-primary/10 font-semibold text-primary">{labelizeT(member.role)}</Badge>
      )}
      {actions != null && <span className="ml-auto flex flex-wrap items-center gap-1.5">{actions}</span>}
    </li>
  );
}

function MemberAction({
  label,
  onClick,
  disabled,
  destructive,
}: {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  destructive?: boolean;
}) {
  return (
    <Button
      type="button"
      variant="outline"
      size="xs"
      onClick={onClick}
      disabled={disabled}
      className={
        destructive
          ? "border-destructive/30 font-medium text-destructive hover:bg-destructive/10 hover:text-destructive"
          : "font-medium text-muted-foreground"
      }
    >
      {label}
    </Button>
  );
}
